import retryAxios from "./axiosRetryQueue";
import { getToken, handle401Response } from "../Utils/tokenManager";
import logger from "../Utils/logger";
import { withDefaultStatus } from "../Utils/Constants/defaultStatus";

export { cleanup as cleanupRetryQueue } from "./axiosRetryQueue";
export const Hipster_Base_URL = process.env.REACT_APP_BASE_URL;

const APICalling = async (endPoint, data = null) => {
	try {
		let token = getToken();
		const panelType = "outlet";
		const fullURL = `${Hipster_Base_URL}${endPoint}?panel=${panelType}`;

		const response = await retryAxios.get(fullURL, {
			params: data,
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});

		return response.data;
	} catch (error) {
		logger.log('[APICalling] Caught error. Status:', error.response?.status, 'Has response?', !!error.response);

		// Handle 401 Unauthorized — attempt token refresh and retry
		if (error.response?.status === 401) {
			logger.log('[APICalling] Got 401, attempting refresh...');
			const refreshResult = await handle401Response();
			logger.log('[APICalling] Refresh result:', refreshResult);
			const { shouldRetry, newToken } = refreshResult;
			if (shouldRetry && newToken) {
				logger.log('[APICalling] Retrying request with new token...');
				try {
					const panelType = "outlet";
					const fullURL = `${Hipster_Base_URL}${endPoint}?panel=${panelType}`;
					const retryResponse = await retryAxios.get(fullURL, {
						params: data,
						headers: {
							Authorization: `Bearer ${newToken}`,
						},
					});
					logger.log('[APICalling] Retry succeeded');
					return retryResponse.data;
				} catch (retryError) {
					logger.error('[APICalling] Retry failed:', retryError.response?.status, retryError.message);
					return retryError;
				}
			} else {
				logger.warn('[APICalling] shouldRetry:', shouldRetry, 'newToken:', newToken);
			}
		} else {
			logger.log('[APICalling] Not a 401 error, returning error as-is');
		}
		return error;
	}
};

const APICallingWithETag = async (endPoint, data = null, etag = null) => {
	try {
		const panelType = "outlet";
		const fullURL = `${Hipster_Base_URL}${endPoint}?panel=${panelType}`;
		let token = getToken();
		const headers = {};

		if (etag && etag !== "0") {
			headers['If-None-Match'] = etag;
		}
		headers['Authorization'] = `Bearer ${token}`;

		const response = await retryAxios.get(fullURL, {
			params: data,
			headers: headers,
			validateStatus: function (status) {
				return (status >= 200 && status < 300) || status === 304;
			}
		});

		return response;
	} catch (error) {
		logger.log('[APICallingWithETag] Caught error. Status:', error.response?.status, 'Has response?', !!error.response);

		// Handle 401 Unauthorized — attempt token refresh and retry
		if (error.response?.status === 401) {
			logger.log('[APICallingWithETag] Got 401, attempting refresh...');
			const refreshResult = await handle401Response();
			logger.log('[APICallingWithETag] Refresh result:', refreshResult);
			const { shouldRetry, newToken } = refreshResult;
			if (shouldRetry && newToken) {
				logger.log('[APICallingWithETag] Retrying request with new token...');
				try {
					const panelType = "outlet";
					const fullURL = `${Hipster_Base_URL}${endPoint}?panel=${panelType}`;
					const headers = {};
					if (etag && etag !== "0") {
						headers['If-None-Match'] = etag;
					}
					headers['Authorization'] = `Bearer ${newToken}`;
					const retryResponse = await retryAxios.get(fullURL, {
						params: data,
						headers: headers,
						validateStatus: function (status) {
							return (status >= 200 && status < 300) || status === 304;
						}
					});
					logger.log('[APICallingWithETag] Retry succeeded');
					return retryResponse;
				} catch (retryError) {
					logger.error('[APICallingWithETag] Retry failed:', retryError.response?.status, retryError.message);
					return retryError;
				}
			} else {
				logger.warn('[APICallingWithETag] shouldRetry:', shouldRetry, 'newToken:', newToken);
			}
		} else {
			logger.log('[APICallingWithETag] Not a 401 error, returning error as-is');
		}
		return error;
	}
};



export const getTherapistList = async (data, etag = null, forceFullResponse = false) => {
	// Always use APICallingWithETag to get access to headers/ETag in response
	return await APICallingWithETag(`/therapists`, data, etag);
};

export const getTherapistLastUpdated = async (data) => {
	return await APICalling(`/cache/last-updated/therapists`, data);
};

export const getTherapistTimings = async (data) => {
	return await APICalling(`/therapist-timings`, data);
};

export const getLevels = async (data) => {
	return await APICalling(`/levels`, data);
};

export const getTimingCodes = async (data) => {
	return await APICalling(`/timing-codes`, data);
};

export const getLeaveStatuses = async (data) => {
	return await APICalling(`/status`, data);
};

export const getNationalities = async (data) => {
	return await APICalling(`/nationalities`, data);
};

export const getCountries = async () => {
	return await APICalling('/countries');
};

// Therapist APIs
export const getTherapistDetails = async (id) => {
	if (!id || id === "null") {
		return null;
	}
	return await APICalling(`/therapists/${id}`);
};

export const createTherapist = async (dataToPost) => {
	return await APICallingPost("/therapists/create", dataToPost);
};

export const updateTherapist = async (id, dataToPost) => {
	return await APICallingPost(`/therapists/${id}`, dataToPost);
};

export const getSelectedOutlet = async (id, data) => {
	if (id === "null") {
		return null;
	} else {
		return await APICalling(`/outlets/${id}`, data);
	}
};


export const getOutletList = async (data) => {
	return await APICalling("/outlets", data);
};

export const getOutletTiming = async (data) => {
	return await APICalling(`/outlet-timings/improved`, data);
};


export const getCustomerList = async (data) => {
	return await APICalling("/users/customer/list", data);
};

export const getClientList = async (data, etag = null, forceFullResponse = false) => {
	if (etag || forceFullResponse) {
		return await APICallingWithETag("/users/customer/list", data, etag);
	}
	return await APICalling("/users/customer/list", data);
};

// Export customers with the same filters as list API (POST)
export const exportCustomers = async (data) => {
    const token = localStorage.getItem("token");
    const panelType = "outlet";
    // Try /users/export instead of /users/customer/export to avoid role check error
    return await retryAxios.post(`${Hipster_Base_URL}/users/customer/export?panel=${panelType}`, data, {
        headers: { 
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        }
    });
};


  

export const getCustomerDetails = async (id) => {
	if (id === "null") {
		return null;
	} else {
		return await APICalling(`/users/${id}`);
	}
};

export const getClientSalesList = async (id, data) => {
	if (!id || id === "null") {
		return null;
	}
	return await APICalling(`/users/${id}/client-sales`, data);
};

export const getGuestCheckinDetails = async (guestCheckinId) => {
	if (!guestCheckinId || guestCheckinId === "null") {
		return null;
	}
	return await APICalling(`/guest-checkins/${guestCheckinId}`);
};

export const getBookingType = async (data) => {
	return await APICalling("/booking-types", data);
};

export const getRoomList = async (data) => {
	return await APICalling("/rooms/check/room-availability", data);
};

export const getBookingsList = async (data) => {
	return await APICalling("/bookings", data);
};

export const getNewBookingsList = async (data) => {
	return await APICalling("/bookings/outlet/booking/list", data);
};

/** Calendar cards for Home grid: GET /calendar/cards?outlet_id=&date=YYYY-MM-DD */
export const getCalendarCards = async (data) => {
	return await APICalling("/calendar/cards", data);
};


// New Sales list API (keep old APIs intact)
export const getSalesList = async (data) => {
	return await APICalling("/sales/list", data);
};

/** GET /sales/:id/details — pass { type: 'membership' } for membership transaction detail */
export const getSalesDetails = async (saleId, queryParams = {}) => {
	if (!saleId || saleId === "null") {
		return null;
	}
	return await APICalling(`/sales/${saleId}/details`, queryParams);
};

export const getSaleRefundPreview = async (saleId) => {
	if (!saleId || saleId === "null") {
		return null;
	}
	return await APICalling(`/sales/${saleId}/refund-preview`);
};

export const getBookingDetail = async (id, data) => {
  return await APICalling(`/bookings/${id}`, data);
};


export const getBookingDetail2 = async (id, data) => {
	return await APICalling(`/bookings/booking-details/${id}`, data);
};


export const getServicesList = async (data) => {
	return await APICalling("/service-category", data);
};

export const getIndividualServicesList = async (data) => {
	return await APICalling("/services", data);
};

// Products list
export const getProductsList = async (data) => {
    return await APICalling("/products", data);
};

export const getSearchResults = async (data) => {
	return await APICalling("/bookings/advance/search", data);
};


export const getPaymentDetails = async (saleId, data) => {
	if (saleId === "null") {
		return null;
	} else {
		return await APICalling(`/payment-transactions/${saleId}`, data);
	}
};

export const getMembershipPaymentDetails = async (saleId) => {
  if (!saleId || saleId === "null") {
    return null;
  }

  return await APICalling(`/payment-transactions/${saleId}`, {
    module: "membership",
  });
};

/** Single payment row by payment-transaction id (e.g. membership View Details). */
export const getPaymentTransactionById = async (paymentTransactionId) => {
	if (paymentTransactionId == null || paymentTransactionId === '' || paymentTransactionId === 'null') {
		return null;
	}
	return await APICalling(`/payment-transactions/${paymentTransactionId}`);
};

export const getCommissionRate = async (data) => {
	return await APICalling("/commission", data);
};
export const getHardTherapistList = async (data) => {
	return await APICalling("/bookings/therapist/queue/daily", data);
};


export const getRoomsForCalendar = async (id, data) => {
	if (id === "null") {
		return null;
	} else {
		return await APICalling(`/room-bookings/outlet/${id}`, data);
	}
};

export const getCalendarFilters = async (data) => {
	return await APICalling("/get-calendar-filters", data);
};

export const getPublicHolidayList = async (data) => {
	return await APICalling("/holidays", data);
};

export const getGuestCheckinList = async (data) => {
	return await APICalling("/guest-checkins", data);
};

export const getDiscountList = async (data) => {
	return await APICalling("/price-adjustments", data);
};

export const getRepeatBookingList = async (id) => {
	return await APICalling(`/bookings/recurring/${id}`);
};

export const getServiceAddonsList = async (data) => {
	return await APICalling("/addon-category/addon/list", data);
};

export const getMembershipPackages = async (data) => {
	return await APICalling("/membership-packages", { ...data, status: 1 });
};

export const getGiftCategoriesByMembershipPackage = async (membershipPackageId, data) => {
	if (!membershipPackageId) {
		return null;
	}
	return await APICalling(`/gift-categories/categories/${membershipPackageId}`, data);
};

export const getMembershipTransactions = async (membershipId, data) => {
	if (!membershipId) {
		return null;
	}
	return await APICalling(`/memberships/${encodeURIComponent(String(membershipId))}/transactions`, data);
};

export const getMembershipTransactionDetail = async (transactionId) => {
	if (!transactionId) {
		return null;
	}
	return await APICalling(`/memberships/transactions/${encodeURIComponent(String(transactionId))}`);
};

export const getPaymentMethods = async (outletId, queryParams = null) => {
	return await APICalling(`/payment-methods/outlet/${outletId}`, queryParams);
};

export const getCustomerCreditDetails = async (customerId) => {
	return await APICalling(`/users/${customerId}/membership/active-credit`);
};


export const getMembershipClientList = async (data) => {
	return await APICalling("/memberships", data);
};




// ======================================================================================================================================
//  POST DATA INTO APIs
// ======================================================================================================================================

const APICallingPost = async (endPoint, data) => {
	try {
		const token = getToken();
		const response = await retryAxios.post(Hipster_Base_URL + endPoint, data, {
			headers: {
				"Content-Type": "application/json",
				"Authorization": `Bearer ${token}`,
			}
		});

		return response.data;
	} catch (error) {
		// Handle 401 Unauthorized — attempt token refresh and retry
		if (error.response?.status === 401) {
			const { shouldRetry, newToken } = await handle401Response();
			if (shouldRetry && newToken) {
				try {
					const retryResponse = await retryAxios.post(Hipster_Base_URL + endPoint, data, {
						headers: {
							"Content-Type": "application/json",
							"Authorization": `Bearer ${newToken}`,
						}
					});
					return retryResponse.data;
				} catch (retryError) {
					return retryError.response?.data || {
						success: false,
						message: "Network error. Please check your internet connection and try again.",
					};
				}
			}
		}
		return (
			error.response?.data || {
				success: false,
				message: "Network error. Please check your internet connection and try again.",
			}
		);
	}
};

export const userLogin = async (loginData) => {
	return await APICallingPost("/login", loginData);
};
export const postCustomerData = async (dataToPost) => {
	return await APICallingPost("/users/create", dataToPost);
};

export const updateCustomerData = async (customerId, dataToPost) => {
	return await APICallingPost(`/users/${customerId}`, dataToPost);
};

export const postUserNotes = async (userId, dataToPost) => {
	return await APICallingPost(`/users/${userId}/notes`, dataToPost);
};

export const emailBookingInvoice = async (bookingId) => {
	return await APICallingPost(`/sales/${bookingId}/invoice/email`, {});
};

// export const postCustomerData = async (dataToPost) => {
//   return await APICallingPost('/users/booking/create', dataToPost);
// };


// export const postCustomerData = async (dataToPost) => {
// 	return await APICallingPost("/otp/send-mail", dataToPost);
// };
// export const postOTP = async (dataToPost) => {
// 	return await APICallingPost("/otp/verify", dataToPost);
// };

export const postBookingData = async (dataToPost) => {
	return await APICallingPost("/bookings/create", dataToPost);
};
export const postToAssignTherapists = async (dataToPost) => {
	return await APICallingPost(`/bookings/check/auto-assign`, dataToPost);
};
export const postpaymentData = async (dataToPost) => {
	return await APICallingPost("/bookings/outlet/payment", dataToPost);
};
export const cancelBookingData = async (dataToPost) => {
	return await APICallingPost("/bookings/item/cancel", dataToPost);
};

export const postBookingStatus = async (dataToPost) => {
	return await APICallingPost("/bookings/update/payment-status", dataToPost);
};

export const refundRequest = async (dataToPost) => {
	return await APICallingPost("/payments/request-refund", dataToPost);
};

export const saveEmployeeAttendance = async (dataToPost) => {
	return await APICallingPost("/therapists/attendance/save", dataToPost);
};

export const saveTherapistSortOrder = async (dataToPost) => {
	return await APICallingPost("/therapists/sort/save", dataToPost);
};

export const editBookingData = async (bookingId, dataToPost) => {
	return await APICallingPost(`/bookings/${bookingId}`, dataToPost);
};

export const postProductPayment = async (dataToPost) => {
	return await APICallingPost("/bookings/product/payment", dataToPost);
};

/** Voucher catalogue (retail voucher SKUs) — separate from /products */
export const getVoucherProductsList = async (data) => {
	return await APICalling("/voucher-products", data);
};

/** POST { serial_number, outlet_id, service_ids } — validates a voucher serial for checkout */
export const postVoucherVerify = async (payload) => {
	return await APICallingPost("/vouchers/verify", payload);
};

export const postMembershipPurchaseTopup = async (dataToPost) => {
	return await APICallingPost("/memberships/purchase-topup", dataToPost);
};

export const postMembershipTransactionPayment = async (membershipId, dataToPost) => {
	const idNum = Number(membershipId);
	return await APICallingPost(
		`/memberships/${encodeURIComponent(String(idNum))}/payments`,
		dataToPost
	);
};

export const postMembershipTransactionNote = async (transactionId, dataToPost) => {
	const idNum = Number(transactionId);
	return await APICallingPost(
		`/memberships/transactions/${encodeURIComponent(String(idNum))}/notes`,
		dataToPost
	);
};

export const postMembershipTransactionRefund = async (transactionId, dataToPost) => {
	const idNum = Number(transactionId);
	return await APICallingPost(
		`/memberships/transactions/${encodeURIComponent(String(idNum))}/refund`,
		dataToPost
	);
};

export const setMembershipPin = async (membershipId, dataToPost) => {
	return await APICallingPost(`/memberships/${membershipId}/set-pin`, dataToPost);
};

export const postMembershipResetPin = async (membershipId, dataToPost) => {
	const idNum = Number(membershipId);
	return await APICallingPost(
		`/memberships/${encodeURIComponent(String(idNum))}/reset-pin`,
		dataToPost
	);
};

export const checkOutletAvailability = async (dataToPost) => {
	return await APICallingPost("/products/outlet-availability/check", dataToPost);
};

export const addSaleNote = async (dataToPost) => {
	return await APICallingPost("/sales/note", dataToPost);
};

export const getTherapistBreaksReport = async (data) => {
	return await APICalling("/breaks/report/list", data);
};

export const getSalesReport = async (params) => {
	return await APICalling("/sales/report", params);
};

export const getSalesSummaryReport = async (params) => {
	return await APICalling("/sales/summary/v2", params);
};

export const getPluReport = async (params) => {
	return await APICalling("/sales/plu", params);
};

export const createPluReportDownload = async (params) => {
	const token = localStorage.getItem("token");
	const response = await retryAxios.get(`${Hipster_Base_URL}/sales/plu/export`, {
		params: { ...params, panel: "outlet" },
		headers: { Authorization: `Bearer ${token}` },
	});
	return response.data;
};

// Payment Summary Report API
export const getPaymentSummaryReport = async (params) => {
	return await APICalling("/payments/payment-summary", params);
};

// Payment Details Report API
export const getPaymentDetailsReport = async (params) => {
	return await APICalling("/payments/payment-details", params);
};

export const createPaymentDetailsReportDownload = async (params) => {
	const token = localStorage.getItem("token");
	const response = await retryAxios.get(`${Hipster_Base_URL}/payments/payment-details/export`, {
		params: { ...params, panel: "outlet" },
		headers: { "Authorization": `Bearer ${token}` }
	});
	return response.data;
};

export const createPaymentSummaryReportDownload = async (params) => {
	const token = localStorage.getItem("token");
	const response = await retryAxios.get(`${Hipster_Base_URL}/payments/payment-summary/export`, {
		params: { ...params, panel: "outlet" },
		headers: { "Authorization": `Bearer ${token}` }
	});
	return response.data;
};

export const createSalesSummaryReportDownload = async (params) => {
	return await APICalling("/sales/summary/v2/report", params);
};

export const getSalesReportDetails = async (saleId, params) => {
	if (!saleId) {
		return null;
	}
	return await APICalling(`/sales/${saleId}/report/details`, params);
};

export const createSalesReportDownload = async (params) => {
	return await APICalling("/sales/report/create", params);
};

export const getSalesReportSignedDownload = async (params) => {
	return await APICalling("/sales/report/signed-download", params);
};

export const getTherapistBreaks = async (data) => {
	return await APICalling("/breaks", data);
};

export const createBreak = async (data) => {
	return await APICallingPost("/breaks/create", data);
};

export const updateBreak = async (breakId, data) => {
	return await APICallingPost(`/breaks/${breakId}`, data);
};

export const deleteBreak = async (breakId) => {
	return await APICallingDelete(`/breaks/${breakId}`);
};

export const updateServiceStatus = async (serviceId, dataToPost) => {
	return await APICallingPost(`/services/${serviceId}`, dataToPost);
};

export const updateTherapistServices = async (therapistId, dataToPost) => {
	return await APICallingPost(`/therapists/update/services?therapist_id=${therapistId}`, dataToPost);
};

export const updateTherapistSchedule = async (dataToPost) => {
	return await APICallingPost("/therapist-timings/update-schedule", dataToPost);
};

export const updateCheckinCheckout = async (dataToPost) => {
	return await APICallingPost("/therapists/update/checkin-checkout", dataToPost);
};

export const updateTherapistStatus = async (therapistId, dataToPost) => {
	return await APICallingPost(`/therapists/${therapistId}/status`, dataToPost);
};

export const setCalendarFilters = async (dataToPost) => {
	const filters = dataToPost?.filters || {};
	return await APICallingPost("/calendar-filters", {
		...dataToPost, filters: {...filters, status: withDefaultStatus(filters.status),},
	});
};


export const getLeaveReport = async (params) => {
	const token = localStorage.getItem("token");
	const response = await retryAxios.get(`${Hipster_Base_URL}/therapists/leave/listing`, {
		params: { ...params, panel: "outlet", pagination: 1 },
		headers: { "Authorization": `Bearer ${token}` }
	});
	return response.data;
};

export const createLeaveReportDownload = async (params) => {
	const token = localStorage.getItem("token");
	return await retryAxios.get(`${Hipster_Base_URL}/therapists/leave/export`, {
		params: { ...params, panel: "outlet" },
		headers: { "Authorization": `Bearer ${token}` }
	});
};

export const getLeaveReportSignedDownload = async (params) => {
	const token = localStorage.getItem("token");
	return await retryAxios.get(`${Hipster_Base_URL}/therapists/leave/signed-download`, {
		params: { ...params, panel: "outlet" },
		headers: { "Authorization": `Bearer ${token}` }
	});
};

export const getBreakReport = async (params) => {
	const token = localStorage.getItem("token");
	const response = await retryAxios.get(`${Hipster_Base_URL}/breaks/report/list`, {
		params: { ...params, panel: "outlet", pagination: 1 },
		headers: { "Authorization": `Bearer ${token}` }
	});
	return response.data;
};

export const createBreakReportDownload = async (params) => {
	const token = localStorage.getItem("token");
	return await retryAxios.get(`${Hipster_Base_URL}/breaks/export`, {
		params: { ...params, panel: "outlet" },
		headers: { "Authorization": `Bearer ${token}` }
	});
};

export const getCancellationReport = async (params) => {
	return await APICalling("/cancell/list", params);
};

// Cancellation report export
export const createCancellationReportDownload = async (params) => {
	return await APICalling("/cancell/cancell-report-export", params);
};

export const getBreakReportSignedDownload = async (params) => {
	const token = localStorage.getItem("token");
	return await retryAxios.get(`${Hipster_Base_URL}/breaks/signed-download`, {
		params: { ...params, panel: "outlet" },
		headers: { "Authorization": `Bearer ${token}` }
	});
};

// Payroll Report APIs
export const getPayrollTherapistList = async (params) => {
	return await APICalling("/sales/list/therapist", params);
};

export const getPayrollDateDetails = async (params) => {
	return await APICalling("/sales/date", params);
};

export const getPayrollItemDetails = async (params) => {
	return await APICalling("/sales/date/item", params);
};

// Payroll export (returns processing status and direct signed download URL)
export const createPayrollReportDownload = async (params) => {
    return await APICalling("/sales/payroll-report-export", params);
};

export const createPayrollAdjustment = async (dataToPost) => {
	return await APICallingPost("/payroll-adjustments", dataToPost);
};

export const getPayrollAdjustments = async (params) => {
	return await APICalling("/payroll-adjustments", params);
};

export const updatePayrollAdjustment = async (id, dataToPost) => {
	return await APICallingPut(`/payroll-adjustments/${id}`, dataToPost);
};

export const deletePayrollAdjustment = async (id) => {
	return await APICallingDelete(`/payroll-adjustments/${id}`);
};

export const createTemporaryBooking = async (params) => {
	return await APICallingPost("/hold/booking", params);
};

export const updateTemporaryBooking = async (params) => {
	return await APICallingPost("/update/hold/booking", params);
};
// Check-in Report APIs
export const getCheckinReportList = async (params) => {
	const token = localStorage.getItem("token");
	const response = await retryAxios.get(`${Hipster_Base_URL}/bookings/checkin/list`, {
		params: { ...params, panel: "outlet" },
		headers: { "Authorization": `Bearer ${token}` }
	});
	return response.data;
};

export const createCheckinReportDownload = async (params) => {
	const token = localStorage.getItem("token");
	return await retryAxios.get(`${Hipster_Base_URL}/bookings/checkin/export`, {
		params: { ...params, panel: "outlet" },
		headers: { "Authorization": `Bearer ${token}` }
	});
};

export const getCheckinReportDetail = async (bookingId) => {
	const token = localStorage.getItem("token");
	const response = await retryAxios.get(`${Hipster_Base_URL}/bookings/checkin/detail/${bookingId}`, {
		params: { panel: "outlet" },
		headers: { "Authorization": `Bearer ${token}` }
	});
	return response.data;
};

export const deleteGuestCheckin = async (guestCheckinId) => {
	const token = localStorage.getItem("token");
	const response = await retryAxios.delete(`${Hipster_Base_URL}/guest-checkins/${guestCheckinId}`, {
		params: { panel: "outlet" },
		headers: { "Authorization": `Bearer ${token}` }
	});
	return response.data;
};

export const postRepeatBookingData = async (params) => {
	return await APICallingPost("/bookings/convert/recurring", params);
};

export const postApplyDiscountToBooking = async (bookingId, dataToPost) => {
	return await APICallingPost(`/bookings/${bookingId}/price-adjustments/apply`, dataToPost);
};

export const postMembershipPassword = async (dataToPost) => {
	return await APICallingPost("/memberships/verify-for-utilization", dataToPost);
};

export const createSale = async (dataToPost) => {
	return await APICallingPost("/bookings/create/sale", dataToPost);
};



// ======================================================================================================================================
//  PUT DATA INTO APIs
// ======================================================================================================================================

const APICallingPut = async (endPoint, data) => {
	try {
		const token = localStorage.getItem("token");
		const response = await retryAxios.put(Hipster_Base_URL + endPoint, data, {
			headers: {
				"Content-Type": "application/json",
				"Authorization": `Bearer ${token}`,
			}
		});

		return response.data;
	} catch (error) {
		return (
			error.response?.data || {
				success: false,
				message: "Network error. Please check your internet connection and try again.",
			}
		);
	}
};

// ======================================================================================================================================
//  DELETE DATA INTO APIs
// ======================================================================================================================================

const APICallingDelete = async (endPoint) => {
	try {
		const token = localStorage.getItem("token");
	  const response = await retryAxios.delete(
		Hipster_Base_URL + endPoint,
		{
		  headers: {
			Accept: "application/json",
			Authorization: `Bearer ${token}`,
		  },
		}
	  );
  
	  return response.data;
	} catch (error) {
	  logger.error("API Call Error:", error);
	  return error.response ? error.response.data : { success: false, message: "Unknown error" };
	}
  };
  
  
export const deleteBooking = async (bookingID, deleteScope) => {
	return await APICallingDelete(`/bookings/destroy/${bookingID}?delete_scope=${deleteScope}`);
};

export const deleteCustomer = async (userId) => {
	return await APICallingDelete(`/users/destroy/${userId}?panel=outlet&user_id=${userId}`);
};

export const deleteHoldBooking = async (bookingId) => {
	return await APICallingDelete(`/delete/hold/booking/${bookingId}`);
};

  
